import {
    DefaultCostRequirementOptions, DefaultRequiredCommentOptions, DefaultSignoffOptions,
    DefaultVorRequiredOptions, FailedOnlyCommentOption, FailedOnlyCostOption,
    FailedOnlySignoffOption, FailedVorRequiredOption,
    FailedOnlyDefectOption, DefaultRequiredDefectOptions, ErMaintenanceSignoffText
} from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";

export const EventTypeHelpers = {
    getEventTypeRequestModel (
        clonedFromEventTypeId,
        eventTypeDetailsModel,
        eventTypeIconModel,
        eventTypeProcessModel,
        eventTypeSettingsModel,
        eventTypeScheduleModel,
        eventTypeCostModel,
        eventTypeChecksModel
    ) {
        const requestModel = {
            // hardcoded to the asset category for now 
            $category: "Asset",
            useLocationVisitingHours: false,
            clonedFromEventTypeId: clonedFromEventTypeId
        };

        setPostModelDetailsFields(requestModel, eventTypeDetailsModel);
        setPostModelIconFields(requestModel, eventTypeIconModel);
        setPostModelProcessFields(requestModel, eventTypeProcessModel);
        setPostModelSettingsFields(requestModel, eventTypeSettingsModel, eventTypeProcessModel);
        setPostModelScheduleFields(requestModel, eventTypeScheduleModel, eventTypeProcessModel);
        setPostModelCostFields(requestModel, eventTypeCostModel, eventTypeProcessModel);
        setPostModelChecksFields(requestModel, eventTypeChecksModel);

        return requestModel;
    },
    cleanupModelsBasedOnProcessModelChanges(
        processModel,
        settingsModel,
        scheduleModel,
        costModel
    ) {
        return {
            processModel: cleanupProcessModelForEventFailureChanges(processModel),
            settingsModel: cleanupSettingsModelForEventFailureChanges(settingsModel),
            scheduleModel: cleanupScheduleModelForEventFailureChanges(scheduleModel),
            costModel: cleanupCostModelForEventFailureChanges(costModel)
        };
    },
    constructAndSetModelFromResponse(response, eventTypeDetailsModel, eventTypeIconModel, eventTypeProcessModel, eventTypeSettingsModel, eventTypeScheduleModel, eventTypeCostModel, eventTypeChecksModel) {
        return {
            details: constructAndSetDetailsModelFromResponse(response, eventTypeDetailsModel),
            icons: constructAndSetIconsModelFromResponse(response, eventTypeIconModel),
            process: constructAndSetProcessModelFromResponse(response, eventTypeProcessModel),
            settings: constructAndSetSettingsModelFromResponse(response, eventTypeSettingsModel),
            schedule: constructAndSetScheduleModelFromResponse(response, eventTypeScheduleModel),
            costs: constructAndSetCostsModelFromResponse(response, eventTypeCostModel),
            checks: constructAndSetChecksModelFromResponse(response, eventTypeChecksModel)
        };
    },
    applyErMaintenanceLockedValues(processModel, settingsModel) {
        return {
            process: {
                ...processModel,
                requiresSignoff: true,
                requiresSignoffValue: "2",
                requiresSignoffFailedOnly: false,
                signoffSecondEnabled: true,
                signoffHelperText: ErMaintenanceSignoffText
            },
            settings: {
                ...settingsModel,
                includeDefect: true
            }
        };
    }
}

function setPostModelDetailsFields(requestModel, eventTypeDetailsModel) {
    requestModel["name"] = eventTypeDetailsModel.name;
    requestModel["conditions"] = eventTypeDetailsModel.additionalProperties
        ? {
            assetTypes: eventTypeDetailsModel?.additionalProperties?.conditions?.assetTypes?.length > 0 ? eventTypeDetailsModel.additionalProperties.conditions.assetTypes : [],
            assetSubTypes: eventTypeDetailsModel?.additionalProperties?.conditions?.assetSubTypes?.length > 0 ? eventTypeDetailsModel.additionalProperties.conditions.assetSubTypes : [],
            assetOwnerships: eventTypeDetailsModel?.additionalProperties?.conditions?.assetOwnerships?.length > 0 ? eventTypeDetailsModel.additionalProperties.conditions.assetOwnerships : [],
            assetStatuses: eventTypeDetailsModel?.additionalProperties?.conditions?.assetStatuses?.length > 0 ? eventTypeDetailsModel.additionalProperties.conditions.assetStatuses : []
        }
        : { };
    requestModel["eventHelperText"] = eventTypeDetailsModel.eventHelperText;
}

function  setPostModelIconFields(requestModel, eventTypeIconModel) {
    requestModel["iconName"] = eventTypeIconModel.eventIconName;
}

function  setPostModelProcessFields(requestModel, eventTypeProcessModel) {
    requestModel["initialEventStatusId"] = eventTypeProcessModel.initialEventStatusId;
    requestModel["failStatusEnabled"] = eventTypeProcessModel.failStatusEnabled;
    requestModel["requiresSignoff"] = eventTypeProcessModel.requiresSignoff;
    requestModel["requiresSignoffFailedOnly"] = eventTypeProcessModel.requiresSignoffFailedOnly;
    requestModel["signoffHelperText"] = eventTypeProcessModel.signoffHelperText;
    requestModel["signoffRestricted"] = eventTypeProcessModel.signoffRestricted;
    requestModel["signoffSecondEnabled"] = eventTypeProcessModel.signoffSecondEnabled;
    requestModel["signoffSecondRestricted"] = eventTypeProcessModel.signoffSecondRestricted;
    requestModel["employeeEdit"] = eventTypeProcessModel.employeeEdit;

    // if fail status is not enabled and the fail status option is chosen then "requiresSignoffFailedOnly" and "requiresSignoff" should be defaulted
    if (eventTypeProcessModel.failStatusEnabled === false && eventTypeProcessModel.requiresSignoffValue === FailedOnlySignoffOption.value) {
        requestModel["requiresSignoffFailedOnly"] = false;
        requestModel["requiresSignoff"] = false;
    }

    // if requires sign-off is false then "signoffHelperText" must be null and "signoffRestricted" should be defaulted
    if (eventTypeProcessModel.requiresSignoff === false && eventTypeProcessModel.requiresSignoffFailedOnly === false) {
        requestModel["signoffHelperText"] = null;
        requestModel["signoffRestricted"] = false;
        requestModel["signoffSecondEnabled"] = false;
        requestModel["signoffSecondRestricted"] = false;
    } 
    else if (eventTypeProcessModel.requiresSignoffFailedOnly === true) {
        // ensure that if requiresSignoffFailedOnly is true then requiresSignoff must be false
        requestModel["requiresSignoff"] = false;
    }
}

function  setPostModelSettingsFields(requestModel, eventTypeSettingsModel, eventTypeProcessModel) {
    requestModel["includeComment"] = eventTypeSettingsModel.includeComment;
    requestModel["requiresComment"] = eventTypeSettingsModel.requiresComment;
    requestModel["requiresCommentFailedOnly"] = eventTypeSettingsModel.requiresCommentFailedOnly;
    requestModel["commentHelperText"] = eventTypeSettingsModel.commentHelperText;
    requestModel["includeDefect"] = eventTypeSettingsModel.includeDefect;
    requestModel["requiresDefect"] = eventTypeSettingsModel.requiresDefect;
    requestModel["requiresDefectFailedOnly"] = eventTypeSettingsModel.requiresDefectFailedOnly;
    requestModel["defectHelperText"] = eventTypeSettingsModel.defectHelperText;
    requestModel["includeDocumentation"] = eventTypeSettingsModel.includeDocumentation;
    requestModel["includeDocumentationFailedOnly"] = eventTypeSettingsModel.includeDocumentationFailedOnly;
    requestModel["requiresDocumentationCount"] = eventTypeSettingsModel.requiresDocumentationCount;
    requestModel["documentationHelperText"] = eventTypeSettingsModel.documentationHelperText;

    // if fail status is not enabled and the fail status option is chosen for comments then all affected fields should be defaulted
    if (eventTypeProcessModel.failStatusEnabled === false) {
        if (eventTypeSettingsModel.requireCommentValue === FailedOnlyCommentOption.value) {
            requestModel["requiresCommentFailedOnly"] = false;
            requestModel["requiresComment"] = false;
        }

        if (eventTypeSettingsModel.requireDefectValue === FailedOnlyDefectOption.value) {
            requestModel["requiresDefectFailedOnly"] = false;
            requestModel["requiresDefect"] = false;
        }

        // if fail status is not enabled and the fail status option is chosen for documentation then all affected fields should be defaulted
        if (eventTypeSettingsModel.includeDocumentationFailedOnly === true) {
            requestModel["includeDocumentationFailedOnly"] = false;
        }
    }    

    // if comments are not enabled (included) then all comment fields should be defaulted
    if (eventTypeSettingsModel.includeComment === false) {
        requestModel["requiresComment"] = false;
        requestModel["requiresCommentFailedOnly"] = false;
        requestModel["commentHelperText"] = null;
    }

    if (eventTypeSettingsModel.requiresCommentFailedOnly === true) {
        requestModel["requiresComment"] = false;
    }

    // if defects are not enabled (included) then all defect fields should be defaulted
    if (eventTypeSettingsModel.includeDefect === false) {
        requestModel["requiresDefect"] = false;
        requestModel["requiresDefectFailedOnly"] = false;
        requestModel["defectHelperText"] = null;
    }

    if (eventTypeSettingsModel.requiresDefectFailedOnly === true) {
        requestModel["requiresDefect"] = false;
    }

    // if documentation is not enabled (included) then all documentation fields should be defaulted
    if (eventTypeSettingsModel.includeDocumentation === false) {
        requestModel["includeDocumentationFailedOnly"] = false;
        requestModel["requiresDocumentationCount"] = 0;
        requestModel["documentationHelperText"] = null;
    }
}

function  setPostModelScheduleFields(requestModel, eventTypeScheduleModel, eventTypeProcessModel) {
    requestModel["adHoc"] = eventTypeScheduleModel.adHoc;
    requestModel["repeatFrequency"] = eventTypeScheduleModel.repeatFrequency;
    requestModel["repeatInterval"] = eventTypeScheduleModel.repeatInterval;
    requestModel["repeatCount"] = eventTypeScheduleModel.repeatCount;
    requestModel["useLocationVisitingHours"] = eventTypeScheduleModel.useLocationVisitingHours;
    requestModel["vorTimer"] = eventTypeScheduleModel.vorTimer;
    requestModel["vorTimerFailedOnly"] = eventTypeScheduleModel.vorTimerFailedOnly;
    requestModel["vorPreDays"] = eventTypeScheduleModel.vorPreDays;
    requestModel["vorPostDays"] = eventTypeScheduleModel.vorPostDays;

    // if the event is adHoc then all schedule fields should be defaulted
    if (eventTypeScheduleModel.adHoc === true) {
        requestModel["repeatFrequency"] = null;
        requestModel["repeatInterval"] = null;
        requestModel["repeatCount"] = null;
        requestModel["useLocationVisitingHours"] = false;
        requestModel["vorTimer"] = false;
        requestModel["vorTimerFailedOnly"] = false;
        requestModel["vorPreDays"] = null;
        requestModel["vorPostDays"] = null;
    }

    // if fail status is not enabled and the fail status option is chosen for vor timer then all affected fields should be defaulted
    if (eventTypeProcessModel.failStatusEnabled === false && eventTypeScheduleModel.vorTimerValue === FailedVorRequiredOption.value) {
        requestModel["vorTimer"] = false;
        requestModel["vorTimerFailedOnly"] = false;
        requestModel["vorPreDays"] = null;
        requestModel["vorPostDays"] = null;
    }

    // if vor timer is not enabled/required then all vor fields should be defaulted
    if (eventTypeScheduleModel.vorTimerValue === "1") {
        requestModel["vorTimer"] = false;
        requestModel["vorTimerFailedOnly"] = false;
        requestModel["vorPreDays"] = null;
        requestModel["vorPostDays"] = null;
    }

    // if vor timer not required then the scheduled days should be defaulted
    if (eventTypeScheduleModel.vorTimer === false && eventTypeScheduleModel.vorTimerFailedOnly === false) {
        requestModel["vorPreDays"] = null;
        requestModel["vorPostDays"] = null;
    }
}

function setPostModelCostFields(requestModel, eventTypeCostModel, eventTypeProcessModel) {
    requestModel["includeCosts"] = eventTypeCostModel.includeCosts;
    requestModel["requiresCosts"] = eventTypeCostModel.requiresCosts;
    requestModel["requiresCostsFailedOnly"] = eventTypeCostModel.requiresCostsFailedOnly;
    requestModel["costHelperText"] = eventTypeCostModel.costHelperText;
    requestModel["includeCostDocumentation"] = eventTypeCostModel.includeCostDocumentation;
    requestModel["requiresCostDocumentationCount"] = eventTypeCostModel.requiresCostDocumentationCount;
    requestModel["includeSuppliers"] = eventTypeCostModel.includeSuppliers;
    requestModel["requiresSuppliers"] = eventTypeCostModel.requiresSuppliers;

    // if fail status is not enabled and the fail status option is chosen for costs then "requiresCosts" and "requiresCostsFailedOnly" should be defaulted
    if (eventTypeProcessModel.failStatusEnabled === false && eventTypeCostModel.requiresCostsValue === FailedOnlyCostOption.value) {
        requestModel["requiresCosts"] = false;
        requestModel["requiresCostsFailedOnly"] = false;

        if ("requiresCostsValue" in eventTypeCostModel) {
            requestModel["requiresCostsValue"] = "1";
        }
    }

    // if costs are not enabled (included) then all cost fields should be defaulted
    if (eventTypeCostModel.includeCosts === false) {
        requestModel["requiresCosts"] = false;
        requestModel["requiresCostsFailedOnly"] = false;
        requestModel["requiresCostsValue"] = "1";
        requestModel["costHelperText"] = '';
        requestModel["includeSuppliers"] = false;
        requestModel["requiresSuppliers"] = false;
        requestModel["includeCostDocumentation"] = false;
        requestModel["requiresCostDocumentationCount"] = 0;
    }

    if (eventTypeCostModel.requiresCostsFailedOnly === true) {
        requestModel["requiresCosts"] = false;
    }
}

function setPostModelChecksFields(requestModel, eventTypeChecksModel) {
    requestModel["relatedTemplateIds"] = eventTypeChecksModel.relatedTemplates?.map(x => { return x.id; });
}

// Event type model cleanup
function cleanupProcessModelForEventFailureChanges(eventTypeProcessModel) {
    const clonedProcessModel = {...eventTypeProcessModel};

    if (clonedProcessModel.requiresSignoffFailedOnly === true) {
        clonedProcessModel["requiresSignoffValue"] = "1";
        clonedProcessModel["requiresSignoffFailedOnly"] = false;
        clonedProcessModel["requiresSignoff"] = false;
        clonedProcessModel["signoffHelperText"] = '';
        clonedProcessModel["signoffRestricted"] = false;
    }

    return clonedProcessModel;
}

function cleanupSettingsModelForEventFailureChanges(eventTypeSettingsModel) {
    const clonedSettingsModel = {...eventTypeSettingsModel};

    if (clonedSettingsModel.requiresCommentFailedOnly === true) {
        clonedSettingsModel["requiresComment"] = false;
        clonedSettingsModel["requiresCommentFailedOnly"] = false;
    }

    if (clonedSettingsModel.requiresDefectFailedOnly === true) {
        clonedSettingsModel["requiresDefect"] = false;
        clonedSettingsModel["requiresDefectFailedOnly"] = false;
    }

    if (clonedSettingsModel.includeDocumentationFailedOnly === true) {
        clonedSettingsModel["includeDocumentationFailedOnly"] = false;
    }

    return clonedSettingsModel;
}

function cleanupScheduleModelForEventFailureChanges(eventTypeScheduleModel) {
    const clonedScheduleModel = {...eventTypeScheduleModel};

    if (clonedScheduleModel.vorTimerFailedOnly === true) {
        clonedScheduleModel["vorTimerValue"] = "1";
        clonedScheduleModel["vorTimer"] = false;
        clonedScheduleModel["vorTimerFailedOnly"] = false;
    }

    return clonedScheduleModel;
}

function cleanupCostModelForEventFailureChanges(eventTypeCostModel) {
    const clonedCostModel = { ...eventTypeCostModel };

    if (clonedCostModel.requiresCostsFailedOnly === true) {
        clonedCostModel.requiresCostsValue = "1";
        clonedCostModel.requiresCosts = false;
        clonedCostModel.requiresCostsFailedOnly = false;
    }

    return clonedCostModel;
}
// Event type model cleanup

// Fetch the event type details and construct all the models
function constructAndSetDetailsModelFromResponse(response, eventTypeDetailsModel) {
    return {
        ...eventTypeDetailsModel,
        adHoc: response.adHoc,
        name: response.name,
        eventHelperText: response.eventHelperText,
        additionalProperties: {
            conditions: {
                assetTypes: response?.conditions?.assetTypes || [],
                assetSubTypes: response?.conditions?.assetSubTypes || [],
                assetOwnerships: response?.conditions?.assetOwnerships || [],
                assetStatuses: response?.conditions?.assetStatuses || []
            }
        }
    };
}

function constructAndSetIconsModelFromResponse(response, eventTypeIconModel) {
    return {
        ...eventTypeIconModel,
        eventIconName: response.iconName
    };
}

function constructAndSetProcessModelFromResponse(response, eventTypeProcessModel) {
    const requiresSignoffValueOption = response.requiresSignoffFailedOnly === true 
        ? FailedOnlySignoffOption 
        : DefaultSignoffOptions.find(o => o.signoffRequired === response.requiresSignoff);

    return {
        ...eventTypeProcessModel,
        initialEventStatusId: response.initialEventStatusId,
        failStatusEnabled: response.failStatusEnabled,
        requiresSignoff: requiresSignoffValueOption?.signoffRequired || response.requiresSignoff,
        requiresSignoffValue: requiresSignoffValueOption?.value || "1",
        signoffHelperText: response.signoffHelperText,
        requiresSignoffFailedOnly: response.requiresSignoffFailedOnly,
        signoffRestricted: response.signoffRestricted,
        signoffSecondEnabled: response.signoffSecondEnabled,
        signoffSecondRestricted: response.signoffSecondRestricted, 
        employeeEdit: response.employeeEdit
    };
}

function constructAndSetSettingsModelFromResponse(response, eventTypeSettingsModel) {
    const requiresCommentValueOption = response.requiresCommentFailedOnly === true
        ? FailedOnlyCommentOption
        : DefaultRequiredCommentOptions.find(o => response.requiresComment === o.requiredComments);

    const requiresDefectValueOption = response.requiresDefectFailedOnly === true
        ? FailedOnlyDefectOption
        : DefaultRequiredDefectOptions.find(o => response.requiresDefect === o.requiredDefects);

    return {
        ...eventTypeSettingsModel,
        includeComment: response.includeComment,
        requireCommentValue: requiresCommentValueOption?.value || "1",
        requiresComment: response.requiresComment,
        requiresCommentFailedOnly: response.requiresCommentFailedOnly,
        commentHelperText: response.commentHelperText,
        includeDefect: response.includeDefect,
        requireDefectValue: requiresDefectValueOption?.value || "1",
        requiresDefect: response.requiresDefect,
        requiresDefectFailedOnly: response.requiresDefectFailedOnly,
        defectHelperText: response.defectHelperText,
        includeDocumentation: response.includeDocumentation,
        includeDocumentationFailedOnly: response.includeDocumentationFailedOnly,
        requiresDocumentationCount: response.requiresDocumentationCount,
        documentationHelperText: response.documentationHelperText,
    };
}

function constructAndSetScheduleModelFromResponse(response, eventTypeScheduleModel) {
    const vorTimerValueOption = response.vorTimerFailedOnly === true
        ? FailedVorRequiredOption
        : DefaultVorRequiredOptions.find(o => o.vorRequired === response.vorTimer);

    return {
        ...eventTypeScheduleModel,
        adHoc: response.adHoc,
        repeatFrequency: response?.repeatFrequency?.toLowerCase() || null,
        repeatInterval: response.repeatInterval,
        repeatCount: response.repeatCount,
        vorTimerValue: vorTimerValueOption?.value || "1",
        vorTimer: response.vorTimer,
        vorTimerFailedOnly: response.vorTimerFailedOnly,
        vorPreDays: response.vorPreDays,
        vorPostDays: response.vorPostDays,
    };
}

function constructAndSetCostsModelFromResponse(response, eventTypeCostModel) {
    const tempRequiresCostsValue = response.requiresCostsFailedOnly === true
        ? "3"
        : DefaultCostRequirementOptions.find(o => response.requiresCosts === true && o.value === "2")?.value || "1";

    return {
        ...eventTypeCostModel,
        includeCosts: response.includeCosts,
        requiresCosts: response.requiresCosts,
        requiresCostsFailedOnly: response.requiresCostsFailedOnly,
        requiresCostsValue: tempRequiresCostsValue,
        costHelperText: response.costHelperText,
        includeSuppliers: response.includeSuppliers,
        requiresSuppliers: response.requiresSuppliers,
        includeCostDocumentation: response.includeCostDocumentation,
        requiresCostDocumentationCount: response.requiresCostDocumentationCount,
    };
}

function constructAndSetChecksModelFromResponse(response, eventTypeChecksModel) {
    return {
        ...eventTypeChecksModel,
        enableChecks: response.relatedTemplates?.length > 0,
        specialProcessingKey: response.specialProcessingKey,
        relatedTemplates: response.relatedTemplates !== null && response.relatedTemplates !== undefined
            ? response.relatedTemplates.map(t => { 
                return { 
                    id: t.templateId, 
                    name: t.templateName
                };                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                   
            })
            : []
    };
}

// Fetch the event type details and construct all the models (END)

